import { useMemo, useState } from 'react';

import type {
  DetailLevel,
  EditActivity,
  MicTestPhase,
  PreviewTab,
} from '../learnBasicsTypes';

import type { OnboardingMicTestState } from './OnboardingContext';

/**
 * Owns the mic-test + Learn-the-basics shared state. Extracted from
 * `OnboardingProvider` to keep that file within the max-lines budget; the
 * stepped Learn-the-basics flow reads `detailLevel` / `evidenceRequested` /
 * `documentRequested` / `noteTab` from here so the left rail and preview stay
 * in sync.
 */
export function useMicTestState(): OnboardingMicTestState {
  const [transcript, setTranscript] = useState('');
  const [phase, setPhase] = useState<MicTestPhase>('recording');
  const [recordingDurationSec, setRecordingDurationSec] = useState(0);
  const [editActivity, setEditActivity] = useState<EditActivity>({
    updatedStyle: false,
    usedEvidence: false,
    createdDocument: false,
  });
  const [hasCompleted, setHasCompleted] = useState(false);
  const [detailLevel, setDetailLevel] = useState<DetailLevel>('medium');
  const [evidenceRequested, setEvidenceRequested] = useState(false);
  const [evidenceQueryIndex, setEvidenceQueryIndex] = useState(0);
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [noteTab, setNoteTab] = useState<PreviewTab>('note');

  return useMemo<OnboardingMicTestState>(
    () => ({
      transcript,
      setTranscript,
      phase,
      setPhase,
      editActivity,
      setEditActivity,
      recordingDurationSec,
      setRecordingDurationSec,
      hasCompleted,
      setHasCompleted,
      detailLevel,
      setDetailLevel,
      evidenceRequested,
      setEvidenceRequested,
      evidenceQueryIndex,
      setEvidenceQueryIndex,
      selectedDocuments,
      setSelectedDocuments,
      noteTab,
      setNoteTab,
    }),
    [
      transcript,
      phase,
      editActivity,
      recordingDurationSec,
      hasCompleted,
      detailLevel,
      evidenceRequested,
      evidenceQueryIndex,
      selectedDocuments,
      noteTab,
    ]
  );
}
